import { HttpAgent } from "@ag-ui/client";
import { API_BASE_URL, pageHeader } from "@appretio/api";
import type { ConversationTurn } from "../types";

/**
 * The chatbot's AG-UI agent.
 *
 * `/v2/chatbot/chat` speaks the AG-UI protocol, so the client is `HttpAgent`
 * rather than a hand-rolled SSE reader: it owns the POST, the event decoding,
 * and — importantly — the conversation itself. `threadId`, `messages` and
 * `state` live on the agent, so nothing here threads message history between
 * turns the way the previous bespoke protocol did.
 */

/**
 * Build an agent for a conversation.
 *
 * The thread id becomes the backend's `conversation_id` — under AG-UI it's part
 * of the request, so the server no longer mints one. Pass the stored
 * `conversationId` when replaying a past conversation so new turns append to it
 * rather than starting a second one; omit it to begin a new thread.
 */
export function createChatAgent(
  seed: ConversationTurn[] = [],
  conversationId?: string | null
): HttpAgent {
  return new HttpAgent({
    url: `${API_BASE_URL}/v2/chatbot/chat`,
    threadId: conversationId ?? crypto.randomUUID(),
    initialMessages: seed.flatMap((turn) => [
      { id: crypto.randomUUID(), role: "user" as const, content: turn.prompt },
      {
        id: crypto.randomUUID(),
        role: "assistant" as const,
        content: turn.response
      }
    ])
  });
}

/**
 * Run one turn: append the user's prompt, publish the page as AG-UI state, and
 * stream the reply through `onDelta`.
 *
 * The auth and page headers are set per run rather than at construction because
 * the token can refresh and the page can change between turns.
 */
export async function runChatTurn(
  agent: HttpAgent,
  prompt: string,
  token: string | null,
  onDelta: (text: string) => void
): Promise<void> {
  agent.headers = { ...agent.headers, ...pageHeader() };
  if (token) {
    agent.headers = { ...agent.headers, Authorization: `Bearer ${token}` };
  } else {
    delete agent.headers.Authorization;
  }
  agent.setState({ page: currentPage() });
  agent.addMessages([
    { id: crypto.randomUUID(), role: "user", content: prompt }
  ]);

  // `runAgent` resolves on RUN_ERROR, so rethrow it for the caller's fallback.
  let runError: string | undefined;
  await agent.runAgent(
    {},
    {
      onTextMessageContentEvent({ event }) {
        onDelta(event.delta);
      },
      onRunErrorEvent({ event }) {
        runError = event.message;
      }
    }
  );
  if (runError !== undefined) throw new Error(runError);
}

/** The current page path + query, best-effort context for the agent. */
function currentPage(): string | undefined {
  return typeof window !== "undefined"
    ? window.location.pathname + window.location.search
    : undefined;
}
